import { useHoverIntent } from '@liveauctioneers/hammer-ui-core/hooks/useHoverIntent';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { WeakLink } from '@liveauctioneers/hammer-ui-core/link';
import { BodySecondary, Caption } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useEffect, useMemo, useState } from 'react';

import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { trackAdUnitClick, trackAdUnitVisible } from '@/redux/modules/analytics';
import { getCatalogAd } from '@/redux/modules/catalog/ad/catalogAd.selectors';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';
import { AmpProduct, useTrackAmpClicked, useTrackAmpSeen } from '@/utils/amplitude/analytics';
import noop from '@/utils/noop';
import { safelyEscapeText } from '@/utils/safelyRender';
import { addImageParams, AddImageParamTypes, getAdQuestionUrl, getCatalogUrl, getSellerUrl } from '@/utils/urls';
import useSSRFriendlyDate from '@/utils/useSSRFriendlyDate';

import { RouteButton } from '../RouteButton/RouteButton';
import { RouteLink, WeakRouteLink } from '../RouteLinks/RouteLinks';

const imageParams: AddImageParamTypes = {
    height: 104,
    width: 104,
};

type Props = {
    adId?: string;
    adType: string;
    className?: string;
    pageName: string;
};

export const CategoryAd = ({ adId, adType, className, pageName }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const trackAmpClicked = useTrackAmpClicked();
    const trackAmpSeen = useTrackAmpSeen();
    const options = {
        ...(adId ? { categoryIds: adId } : null),
        pageName: pageName,
    };
    const {
        adUnitId,
        catalogId,
        catalogTitle,
        destination,
        imageUrl1,
        imageUrl2,
        imageUrl3,
        saleStartTs,
        sellerId,
        sellerName,
    } = useAppSelector((state) => getCatalogAd(state, options));

    const [catalogLoaded, setCatalogLoaded] = useState(false);

    const clientSaleStartTs = useSSRFriendlyDate(saleStartTs);

    const goTo = destination || getCatalogUrl(catalogId, catalogTitle);
    const images = useMemo(() => {
        return [imageUrl1, imageUrl2, imageUrl3].filter((image) => Boolean(image));
    }, [imageUrl1, imageUrl2, imageUrl3]);
    const sellerUrl = getSellerUrl(sellerId, sellerName);

    const hideButton = useIsNarrowerThan(904);

    useEffect(() => {
        if (adUnitId) {
            dispatch(
                trackAdUnitVisible({
                    adUnitId,
                    page: adType,
                })
            );
            trackAmpSeen({ ampProduct: AmpProduct.LeaderboardAd, catalogId, houseId: sellerId });
        }
    }, [dispatch, adUnitId, catalogId, adType, sellerId, trackAmpSeen]);

    const trackClick = useCallback(() => {
        if (adUnitId) {
            dispatch(
                trackAdUnitClick({
                    adUnitId,
                    consignment: false,
                    page: adType,
                })
            );
            trackAmpClicked({ ampProduct: AmpProduct.LeaderboardAd, catalogId, houseId: sellerId });
        }
    }, [dispatch, adUnitId, catalogId, adType, sellerId, trackAmpClicked]);

    const mapImages = useCallback((url: string) => {
        return (
            <div
                className="mr-3xs flex h-[52px] w-[52px] basis-[52px] overflow-hidden bg-background-primary [&:last-child]:mr-0"
                key={`${url}-catalog-banner-ad-image`}
            >
                <img
                    className="flex flex-col overflow-hidden"
                    height={String(imageParams.height / 2)}
                    src={addImageParams({ ...imageParams, url })}
                    width={String(imageParams.width / 2)}
                />
            </div>
        );
    }, []);

    const loadCatalog = useCallback(() => {
        if (!catalogLoaded) {
            dispatch(fetchCatalogsIfNeeded({ catalogIds: [catalogId] }));
            setCatalogLoaded(true);
        }
    }, [catalogId, catalogLoaded, dispatch]);

    const hoverIntentRef = useHoverIntent({
        interval: 50,
        onMouseOut: noop,
        onMouseOver: loadCatalog,
        sensitivity: 5,
        timeout: 50,
    });

    // do not render on mobile or if no ad is present
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = mergedCaterwaulAndHammerUIThemes;
    const mobile = useIsNarrowerThan(tabletNarrowWidthNum + 1);
    if (!adUnitId || mobile) {
        return null;
    }

    return (
        <div
            className={cn('mx-auto w-full border-b border-border-secondary bg-background-tertiary py-sm', className)}
            data-testid="ad-container"
            onClick={trackClick}
            ref={hoverIntentRef}
        >
            <div className="relative mx-auto flex w-full max-w-[1240px] items-center px-sm">
                <RouteLink
                    className="absolute bottom-0 left-0 right-0 top-0"
                    to={goTo}
                >
                    {' '}
                </RouteLink>
                <div className="mr-md flex h-[52px] w-[156px] basis-[156px]">{images.map(mapImages)}</div>
                <div className="flex flex-col overflow-hidden">
                    <BodySecondary className="mb-2xs max-w-[500px] overflow-hidden overflow-ellipsis whitespace-nowrap">
                        {safelyEscapeText(catalogTitle)}
                    </BodySecondary>
                    <div className="flex gap-sm">
                        <Caption
                            className="whitespace-nowrap"
                            color="secondary"
                        >
                            {clientSaleStartTs}
                        </Caption>
                        <Caption color="secondary">|</Caption>
                        <WeakRouteLink
                            className="pointer-events-[all] z-[1] max-w-[120px] overflow-hidden overflow-ellipsis whitespace-nowrap"
                            size="xs"
                            to={sellerUrl}
                        >
                            {safelyEscapeText(sellerName)}
                        </WeakRouteLink>
                        <Caption color="secondary">|</Caption>
                        <Caption
                            className="pointer-events-none relative z-[1] whitespace-nowrap"
                            color="secondary"
                        >
                            <FormattedMessage
                                id="advertisement"
                                tagName="span"
                            />{' '}
                            <WeakLink
                                className="pointer-events-[all] z-[1]"
                                href={getAdQuestionUrl()}
                                size="xs"
                                target="_blank"
                            >
                                <FormattedMessage id="your_ad_here" />
                            </WeakLink>
                        </Caption>
                    </div>
                </div>
                {!hideButton && (
                    <RouteButton
                        className="ml-auto"
                        data-testid="explore-auction-button"
                        size="sm"
                        to={goTo}
                        variant="transparent"
                    >
                        {formatMessage({ id: 'explore_auction' })}
                    </RouteButton>
                )}
            </div>
        </div>
    );
};
